import { useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import GemLackModal from 'src/components/GemLackModal';
import PermissionErrorFullModal from 'src/components/PermissionErrorFullModal';
import useCallbackWithCheckLogin from 'src/hooks/useCallbackWithCheckLogin';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { Routes } from 'src/router/route';
import { hasFailedToSetServersInfoAtom } from 'src/stores/auth/atoms';
import { showNetworkErrorReloadModalAtom } from 'src/stores/error/atoms';
import { grantVideoAtom, getMatchLackingGemAmountAtom, startFindingMatchAtom, getGemLackTriggersFromMatchFiltersAtom, isGrantVideoPendingAtom, statusAtom, matchFilterSelectionsAtom } from 'src/stores/match/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE, EventLoginModalVisitorTrigger } from 'src/types/Event';
import { STATUS } from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
import DeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { SilentAudioPlayer } from 'src/utils/media/silent-audio-player';
type Props = {
  trigger?: EventLoginModalVisitorTrigger;
  executeOnLogin?: boolean;
};
const useStartMatch = ({
  trigger,
  executeOnLogin = false
}: Props = {}) => {
  const router = useRouter();
  const {
    selectedTab
  } = useSelectedTab();
  const startFindingMatch = useSetAtom(startFindingMatchAtom);
  const grantVideo = useSetAtom(grantVideoAtom);
  const getGemLackTriggersFromMatchFilters = useSetAtom(getGemLackTriggersFromMatchFiltersAtom);
  const getMatchLackingGemAmount = useSetAtom(getMatchLackingGemAmountAtom);
  const showModal = useSetAtom(showModalAtom);
  const isGrantVideoPending = useAtomValue(isGrantVideoPendingAtom);
  const hasFailedToSetServersInfo = useAtomValue(hasFailedToSetServersInfoAtom);
  const showNetworkErrorReloadModal = useSetAtom(showNetworkErrorReloadModalAtom);
  const setMatchFilterSelections = useSetAtom(matchFilterSelectionsAtom);
  const handleGemLack = (lackingGemAmount: number) => {
    const gemLackTriggers = getGemLackTriggersFromMatchFilters();
    return showModal({
      key: ModalType.GEM_LACK,
      component: () => GemLackModal({
        lackingGemAmount,
        gemLackTriggers
      })
    });
  };
  const onGrantVideo = () => {
    if (DeviceInfo().isWebKit) {
      SilentAudioPlayer.Instance.start();
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT,
        eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_CHAT
      });
    }
    startFindingMatch();
  };
  const onGrantVideoError = (error: Error) => {
    if (error.name === 'NotAllowedError') {
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'media_permission_error',
          raw: error
        }
      });
    } else if (error.name === 'no getUserMedia') {
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'no_get_user_media',
          raw: error
        }
      });
    } else {
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'unknown_media_grant_error',
          raw: error
        }
      });
    }
  };
  const status = useAtomValue(statusAtom);
  const canStartMatch = useMemo(() => {
    return status === STATUS.INITIAL && !isGrantVideoPending;
  }, [isGrantVideoPending, status]);
  const startMatch = useCallbackWithCheckLogin(async () => {
    if (hasFailedToSetServersInfo) {
      showNetworkErrorReloadModal();
      return;
    }
    setMatchFilterSelections(prev => prev.filter(({
      filterId
    }) => filterId !== 'MATCH_BOOST'));
    const matchLackingGemAmount = getMatchLackingGemAmount();
    if (matchLackingGemAmount > 0) {
      handleGemLack(matchLackingGemAmount);
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'gem_lack_in_client'
        }
      });
      return;
    }
    if (selectedTab.name !== 'mirror') {
      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'match_in_not_mirror_tab'
        }
      });
      router.push(Routes['/']());
    }
    await grantVideo({
      onSuccess: onGrantVideo,
      onError: onGrantVideoError,
      permissionErrorModal: PermissionErrorFullModal
    });
  }, {
    trigger,
    executeOnLogin
  });
  return {
    startMatch,
    canStartMatch
  };
};
export default useStartMatch;